import { defineStore, storeToRefs } from 'pinia'
import { shallowRef, watch } from 'vue'
import { useHabilitationsAdminEngineStore, type Entite } from './useHabilitationsAdminEngineStore'
import {
  HabilitationAddErreurDoublon,
  type ContactDTO,
  type HabilitationAddDTO,
  type HabilitationDTO,
} from '@/features/api/habilitation/dto'
import { useImmerFieldPatcher } from '@/features/interface/composables'
import type { NiveauPermission, TypePermission } from '@/features/domaine/types/opco'

export const useHabilitationsAdminContentStore = defineStore('habilitations-admin-content', () => {
  const habilitations = shallowRef<HabilitationDTO[]>()

  const engine = useHabilitationsAdminEngineStore()
  const { isReady, isReadonly, identifiantInterneApi, engineState, estModeEntiteSirenSiret, dataToken, allowNavigate } =
    storeToRefs(engine)

  async function loadHabilitations() {
    if (isReady.value && identifiantInterneApi.value) {
      const service = engine.getService()
      habilitations.value = (await service.getHabilitations(identifiantInterneApi.value)) ?? []
    } else {
      habilitations.value = undefined
    }
  }

  const { setupCustomUpdater } = useImmerFieldPatcher<{ habilitations: HabilitationDTO[] | undefined }>({
    getModel: () => ({ habilitations: habilitations.value }),
    async setModel(model, patchs) {
      // console.log("patch", patchs)
      const service = engine.getService()

      let firstDeleteIndex: number | undefined = undefined
      for (const patch of patchs) {
        if (patch.op === 'replace' && patch.path[3] === 'permissions') {
          // Modification permissions
          const index = patch.path[1] as number
          const habilitation = model.habilitations!.at(index)!

          await service.updateHabilitationPermissions(habilitation)
        } else if (patch.op === 'replace' && patch.path[3] === 'statut') {
          // Modification du statut
          const index = patch.path[1] as number
          const habilitation = model.habilitations!.at(index)!

          await service.updateHabilitationPermissions(habilitation)
        } else if (patch.path[0] === 'habilitations') {
          if (!firstDeleteIndex && patch.op === 'replace') {
            // Capture les déplacements d'habilitations
            firstDeleteIndex = patch.path[1] as number
          } else if (patch.op === 'remove') {
            // Supression habilitation
            const index = firstDeleteIndex ?? (patch.path[1] as number)
            firstDeleteIndex = undefined

            const habilitation = habilitations.value!.at(index)!

            await service.deleteHabilitation(habilitation)
          }
        }
      }

      habilitations.value = model.habilitations
    },
  })

  async function addHabilitation(value: Entite | ContactDTO): Promise<HabilitationDTO | undefined> {
    const service = engine.getService()

    const commonModel: Pick<HabilitationAddDTO, 'typeEntreprise' | 'publics' | 'statut'> = {
      typeEntreprise: engine.engineState.context === 'adherent' ? 'Adherent' : 'Prestataire',
      publics: [{ type: 'Permanent' }],
      statut: 'Membre',
    }

    const entiteModel: HabilitationAddDTO =
      'email' in value
        ? {
            identifiantEntiteJuridique: engine.identifiantInterneApi!,
            typeEntiteJuridique: engine.identifiantInterneApi!.length === 9 ? 'Entreprise' : 'Etablissement',
            userId: value.userId ?? value.email.toLocaleLowerCase(),
            ...commonModel,
          }
        : {
            identifiantEntiteJuridique: value.identifiant,
            typeEntiteJuridique: value.identifiant.length === 9 ? 'Entreprise' : 'Etablissement',
            userId: engine.identifiantInterneApi!,
            ...commonModel,
          }

    try {
      const habilitation = await service.addHabilitation(entiteModel, { dialogMode: 'retry' })
      await setupCustomUpdater<HabilitationDTO>((patch, habilitation) => {
        patch.habilitations?.push(habilitation)
      })(habilitation)

      return habilitation
    } catch (err) {
      if (err instanceof HabilitationAddErreurDoublon) {
        // Il faut recharger les habilitations
        await loadHabilitations()
      }
    }
  }

  const setHabilitationMembre = setupCustomUpdater<HabilitationDTO>((patch, habilitation) => {
    const habititationsPatch = patch.habilitations?.find((h) => h.id === habilitation.id)
    if (habititationsPatch?.autorisations.statut !== 'Visiteur') return
    habititationsPatch.autorisations.statut = 'Membre'
  })

  const updateNiveauPermission = setupCustomUpdater<{
    habilitation: HabilitationDTO
    type: TypePermission
    niveau: NiveauPermission
  }>((patch, options) => {
    const habititationsPatch = patch.habilitations?.find((h) => h.id === options.habilitation.id)
    const permission = habititationsPatch?.autorisations?.permissions?.find((h) => h.type === options.type)
    if (!permission) return

    // Mise en cohérence du statut d'autorisation avec les permissions
    if (permission.type === 'Administration') {
      // Sécurité : empéche un admin de se retirer les droits
      // d'admin à lui meme
      if (habititationsPatch?.userId === engine.dataToken?.userId) return

      if (options.niveau === 'Ecriture') {
        habititationsPatch!.autorisations.statut = 'Administrateur'
      } else {
        habititationsPatch!.autorisations.statut = 'Membre'
      }
    }

    permission.niveau = options.niveau
  })

  const deleteHabilitation = setupCustomUpdater<HabilitationDTO>((patch, habilitation) => {
    const index = patch.habilitations?.findIndex((h) => h.id === habilitation.id)
    if (index === undefined) return
    patch.habilitations?.splice(index, 1)
  })

  watch(() => isReady.value, loadHabilitations, { immediate: true })

  return {
    isReadonly,
    addHabilitation,
    setHabilitationMembre,
    dataToken,
    deleteHabilitation,
    engineState,
    estModeEntiteSirenSiret,
    habilitations,
    setupCustomUpdater,
    searchContact: engine.searchContact,
    searchEntite: engine.searchEntite,
    allowNavigate,
    navigateTo: engine.navigateTo,
    updateNiveauPermission,
  }
})
